import {
  isPlatformSettingEnabled,
  SETTING_SIDEBAR_DASHBOARD,
  SETTING_SIDEBAR_EINSTELLUNGEN,
  SETTING_SIDEBAR_ZUSAMMENARBEIT
} from "common/platformSettings";
import { PLATFORM_MODULE, VISIBLE_FOR } from "./platformSidebarConstants";
import {
  getProjectsForCompany,
  WENCO_LOGISTIC_COMPANY_ID
} from "./platformSidebarProjects";

export const platformSidebarNavGroups = [
  {
    label: "Beziehung",
    visibleFor: VISIBLE_FOR.ALL,
    items: [
      {
        key: PLATFORM_MODULE.COLLABORATION,
        label: "Zusammenarbeit",
        title: "Zusammenarbeit",
        icon: "users",
        visibleFor: VISIBLE_FOR.ALL,
        action: "navigate",
        settingId: SETTING_SIDEBAR_ZUSAMMENARBEIT
      }
    ]
  },
  {
    label: "Projekte",
    visibleFor: VISIBLE_FOR.ALL,
    dynamic: "projects",
    items: []
  },
  {
    label: "Module & Services",
    visibleFor: VISIBLE_FOR.ALL,
    items: [
      {
        key: PLATFORM_MODULE.DISPOSITION,
        label: "Arbeitsmappen",
        title: "Arbeitsmappen — nicht aktiviert",
        icon: "clipboard-list",
        visibleFor: VISIBLE_FOR.ALL,
        action: "none",
        inactive: true,
        badge: "Inaktiv"
      },
      {
        key: PLATFORM_MODULE.TOURS,
        label: "Tourenoptimierung",
        title: "Tourenoptimierung — nicht aktiviert",
        icon: "route",
        visibleFor: VISIBLE_FOR.ALL,
        action: "none",
        inactive: true,
        badge: "Inaktiv"
      },
      {
        key: PLATFORM_MODULE.DOCUMENTS,
        label: "Dokumentenverwaltung",
        title: "Dokumentenverwaltung — nicht aktiviert",
        icon: "file",
        visibleFor: VISIBLE_FOR.ALL,
        action: "none",
        inactive: true,
        badge: "Inaktiv"
      },
      {
        key: PLATFORM_MODULE.SETTINGS,
        label: "Einstellungen",
        title: "Einstellungen",
        icon: "settings",
        visibleFor: VISIBLE_FOR.ALL,
        action: "settings",
        settingId: SETTING_SIDEBAR_EINSTELLUNGEN,
        // Wenco-Kunden: kein Einstellungen. TrackCode-Intern: nur Profilmenü.
        hideForCompanyIds: [WENCO_LOGISTIC_COMPANY_ID],
        hideInNavForInternal: true
      },
      {
        key: "modules-more",
        label: "weitere auf Anfrage",
        title: "Weitere Module und Services auf Anfrage",
        icon: "plus",
        visibleFor: VISIBLE_FOR.ALL,
        action: "none",
        hint: true
      }
    ]
  },
  {
    label: "Interne Arbeitsdokumente",
    visibleFor: VISIBLE_FOR.INTERNAL,
    pin: "bottom",
    items: [
      {
        key: PLATFORM_MODULE.INTERNAL_WENCO_TOURS,
        label: "Wenco Tourenoptimierung",
        title: "Wenco Tourenoptimierung",
        icon: "file",
        visibleFor: VISIBLE_FOR.INTERNAL,
        action: "navigate",
        hideOnTestEnvironment: true
      },
      {
        key: PLATFORM_MODULE.DASHBOARD,
        label: "Dashboard",
        title: "Rechnungs-Dashboard",
        icon: "chart-bar",
        visibleFor: VISIBLE_FOR.INTERNAL,
        action: "navigate",
        settingId: SETTING_SIDEBAR_DASHBOARD,
        hideOnTestEnvironment: true
      }
    ]
  }
];

export function isNavVisibleFor(visibleFor, { isInternalUser }) {
  if (visibleFor === VISIBLE_FOR.INTERNAL) {
    return isInternalUser;
  }
  return true;
}

export function isNavItemHiddenForCompany(item, logisticCompanyId) {
  if (!item.hideForCompanyIds?.length) {
    return false;
  }
  return item.hideForCompanyIds.includes(logisticCompanyId);
}

export function isNavItemHiddenInNav(item, { isInternalUser, logisticCompanyId }) {
  if (item.hideInNavForInternal && isInternalUser) {
    return true;
  }
  return isNavItemHiddenForCompany(item, logisticCompanyId);
}

export function isNavItemHiddenOnEnvironment(item, { isTestEnvironment = false } = {}) {
  return Boolean(item.hideOnTestEnvironment && isTestEnvironment);
}

export function isNavItemEnabled(item, settings) {
  if (!item.settingId) {
    return true;
  }
  // Maßgeblich sind die Settings des übergeordneten (Heimat-)Mandanten. Damit
  // bleibt die Modul-Sichtbarkeit für einen Übermandanten an seinen eigenen
  // Mandanten gekoppelt – auch nach Wechsel in einen Kundenmandanten, dessen
  // Settings ein Modul ausblenden würden.
  return isPlatformSettingEnabled(settings, item.settingId);
}

function buildProjectNavItems(logisticCompanyId) {
  return getProjectsForCompany(logisticCompanyId).map(project => ({
    key: project.key,
    label: project.label,
    title: project.title,
    icon: project.icon,
    visibleFor: VISIBLE_FOR.ALL,
    action: "navigate",
    badge: project.badge
  }));
}

export function getVisibleNavGroups(groups, context) {
  const { logisticCompanyId } = context;
  return groups
    .filter(group => isNavVisibleFor(group.visibleFor, context))
    .map(group => {
      const baseItems =
        group.dynamic === "projects"
          ? buildProjectNavItems(logisticCompanyId)
          : group.items;
      return {
        ...group,
        items: baseItems.filter(
          item =>
            isNavVisibleFor(item.visibleFor, context) &&
            !isNavItemHiddenInNav(item, context) &&
            !isNavItemHiddenOnEnvironment(item, context) &&
            isNavItemEnabled(item, context.settings)
        )
      };
    })
    .filter(group => group.items.length > 0);
}

/** Sidebar nur bei „Zusammenarbeit“ aufgeklappt; sonst zugeklappt. */
export function isPlatformSidebarCollapsedForPath(pathname) {
  return getActiveModule(pathname) !== PLATFORM_MODULE.COLLABORATION;
}

export function getActiveModule(pathname) {
  if (pathname.startsWith("/collaboration")) {
    return PLATFORM_MODULE.COLLABORATION;
  }
  if (pathname.startsWith("/projects/pilot-tourenoptimierung-1")) {
    return "project-pilot-tourenoptimierung-1";
  }
  if (
    pathname === "/projects/pilot-tourenoptimierung" ||
    pathname.startsWith("/projects/pilot-tourenoptimierung/")
  ) {
    return "project-pilot-tourenoptimierung";
  }
  if (pathname.startsWith("/internal/wenco-tourenoptimierung")) {
    return PLATFORM_MODULE.INTERNAL_WENCO_TOURS;
  }
  if (pathname.startsWith("/admin/dashboard")) {
    return PLATFORM_MODULE.DASHBOARD;
  }
  if (pathname.startsWith("/management")) {
    return PLATFORM_MODULE.MANAGEMENT;
  }
  if (pathname.startsWith("/scheduling")) {
    return PLATFORM_MODULE.DISPOSITION;
  }
  return PLATFORM_MODULE.COLLABORATION;
}

export function getModulePath(moduleKey) {
  switch (moduleKey) {
    case PLATFORM_MODULE.COLLABORATION:
      return "/collaboration";
    case PLATFORM_MODULE.DASHBOARD:
      return "/admin/dashboard";
    case PLATFORM_MODULE.MANAGEMENT:
      return "/management";
    case PLATFORM_MODULE.DISPOSITION:
      return "/scheduling";
    case PLATFORM_MODULE.INTERNAL_WENCO_TOURS:
      return "/internal/wenco-tourenoptimierung";
    case "project-pilot-tourenoptimierung":
      return "/projects/pilot-tourenoptimierung";
    case "project-pilot-tourenoptimierung-1":
      return "/projects/pilot-tourenoptimierung-1";
    default:
      return null;
  }
}
